<template>
  <BaseTemplate :currentUser="currentUser">
    <div class="my-page">
      <Title eng="MYPAGE" :jpn="t('マイページ')" />
      <div class="name_area">
        <span>{{ lastName }} {{ firstName }} {{t('さん')}}</span>
      </div>
      <div class="address_change">
        <a href="https://www.iranoan.com/contacts" target="_blank">住所変更をご希望の際はお問合せください</a>
      </div>
      <GrayBack class="gray_area">
        <div class="label">
          <span>{{t('保有ポイント')}}</span>
          <div class="point_area">
            <span>{{ point | comma }}</span>
            <span>{{t('ポイント')}}</span>
          </div>
        </div>
        <div class="button_area">
          <BlackGradationButton
            class="square-button"
            :text="t('ポイント購入')"
            @handleSquareBtnClick="goToPointBuy"
          />
        </div>
      </GrayBack>
      <BarGraph
        :maxValue="rankNextPrice"
        :currentValue="rankCurrentPrice"
        :rankName="rankName"
      />
      <div v-if="isRankMaintained" class="button_area">
        <BlackGradationButton
          class="square-button"
          text="翌月のランクを維持"
          @handleSquareBtnClick="clickMaintainRank"
        />
      </div>
      <div class="link_area">
        <div class="link" @click="goToPointHistory">ポイント履歴</div>
        <div class="link" @click="goToBuyProductHistory">商品購入履歴</div>
        <div class="link" @click="goToUserGuide">ご利用ガイド</div>
        <div class="link" @click="goContactUs">お問い合わせ</div>
        <div class="link" @click="goToUserMenu">登録情報設定</div>
      </div>
      <div class="button_area mt-40">
        <BlackGradationButton
          class="square-button logout"
          :text="t('ログアウト')"
          @handleSquareBtnClick="logout"
        >
          <img src="@/assets/logout.png" alt="ログアウト画像">
        </BlackGradationButton>
      </div>
    </div>
    <CommonOverlay class="overlay" v-show="isOpenConfirm && isRankMaintained">
      <CommonModal class="modal pack-confirm">
        <span class="close" @click="isOpenConfirm = false">&times;</span>
        <div class="modal-title">
          <span>翌月のランク維持</span>
        </div>
        <div class="bay_area">
          <div class="modal-subtitle">
            <span>翌月の開始ランク</span>
          </div>
          <div class="use_num">
            <span>{{ rankNextMonthName }}</span>
          </div>
        </div>
        <div class="bay_area">
          <div class="modal-subtitle">
            <span>使用コイン</span>
          </div>
          <div class="use_num">
            <span>{{ rankCoin | comma }}C</span>
          </div>
          <div class="modal-subtitle">
            <span>保有コイン</span>
          </div>
          <div class="use-point">
            <div class="point-area">{{ currentUser.coin | commaFloat }}C</div>
            <div>→</div>
            <div class="point-area">{{ currentUser.coin - rankCoin | commaFloat }}C</div>
          </div>
        </div>
        <div class="modal-button-area">
          <BlackGradationButton class="bay-button" text="翌月のランクを維持する" @handleSquareBtnClick="buyRank" />
        </div>
      </CommonModal>
    </CommonOverlay>
    <CommonOverlay class="overlay" v-show="isOpenConp">
      <CommonModal class="modal pack-confirm">
        <span class="close" @click="isOpenConp = false">&times;</span>
        <div class="modal-title">
          <span>翌月のランク維持完了</span>
        </div>
        <div class="modal-title">
          <span>翌月の開始ランクを{{ rankNextMonthName }}に維持しました。</span>
        </div>
        <div class="modal-button-area">
          <BlackGradationButton class="bay-button" text="閉じる" @handleSquareBtnClick="isOpenConp = false" />
        </div>
      </CommonModal>
    </CommonOverlay>
  </BaseTemplate>
</template>

<script>
import { mapGetters } from 'vuex';
import ClickOutside from 'vue-click-outside';
import {
  separateCharacterNumber,
  separateDecimalNumber,
} from '@/common/util/format';

import BaseTemplate from '@/components/templates/BaseTemplate.vue';
import BlackGradationButton from '@/components/atoms/BlackGradationButton.vue';
import Title from '@/components/molecules/Title.vue';
import t from '@/plugins/i18n';
import GrayBack from '@/components/molecules/GrayBack.vue';
import BarGraph from '@/components/atoms/BarGraph.vue';
import CommonOverlay from '@/components/atoms/CommonOverlay.vue';
import CommonModal from '@/components/atoms/CommonModal.vue';

export default {
  name: 'MyPage',
  mixins: [t],

  components: {
    BaseTemplate,
    BlackGradationButton,
    Title,
    GrayBack,
    BarGraph,
    CommonOverlay,
    CommonModal,
  },

  directives: {
    ClickOutside,
  },

  filters: {
    comma(value) {
      return separateCharacterNumber(parseInt(value, 10));
    },
    commaFloat(value) {
      return separateDecimalNumber(value, true);
    },
  },

  data: () => ({
    lastName: null,
    firstName: null,
    point: null,
    rankName: null,
    rankNextPrice: 0,
    rankCurrentPrice: 0,
    rankCoin: 0,
    rankNextMonthName: '',
    rankNextMonth: 1,
    isRankMaintained: false,
    isOpenConfirm: false,
    isOpenConp: false,
  }),

  computed: {
    ...mapGetters({
      prefectureList: 'prefecture/prefectureList',
      currentUser: 'user/currentUser',
      currentUserDetail: 'user/currentUserDetail',
      currentAddress: 'zipCode/currentAddress',
      changeUserInformationResult: 'user/changeUserInformationResult',
      logOutUserResult: 'user/logOutUserResult',
      buyRankResult: 'user/buyRankResult',
    }),
  },

  watch: {
    currentUserDetail(newResult) {
      this.$nextTick(() => {
        this.lastName = newResult.lastName;
        this.firstName = newResult.firstName;
        this.point = newResult.availablePoint;
        this.rankName = newResult.rank_name;
        this.rankCoin = newResult.rank_coin;
        this.rankNextPrice = newResult.rank_max_price;
        this.rankCurrentPrice = newResult.rank_current_price;
        this.isRankMaintained = newResult.is_rank_maintained;
        this.rankNextMonthName = newResult.next_month_name;
        this.rankNextMonth = newResult.next_month;
      });
    },
    currentAddress(newVal) {
      if (newVal) {
        if (newVal.state === 200) {
          const { result } = newVal;
          this.selectPrefectureId(result[0].prefcode);
          this.inputShiku(`${result[0].address2}${result[0].address3}`);
        }
      }
    },
    changeUserInformationResult(newVal) {
      if (newVal) {
        if (newVal.state === 200) {
          this.$store.dispatch('user/clearChangeUserInformationResult');
          this.isShowAlertSuccess = true;
        } else {
          const {
            lastName, firstName, middleName, tel, mail, zipcode,
            pref_id: prefId, shiku, address, building,
          } = newVal.errors;
          this.errorMessageLastName = lastName ? lastName[0] : null;
          this.errorMessageMiddleName = middleName ? middleName[0] : null;
          this.errorMessageFirstName = firstName ? firstName[0] : null;
          this.errorMessageTel = tel ? tel[0] : null;
          this.errorMessageMail = mail ? mail[0] : null;
          this.errorMessageZipCode = zipcode ? zipcode[0] : null;
          this.errorMessagePrefectureId = prefId ? prefId[0] : null;
          this.errorMessageShiku = shiku ? shiku[0] : null;
          this.errorMessageAddress = address ? address[0] : null;
          this.errorMessageBuilding = building ? building[0] : null;
        }
      }
    },
    logOutUserResult(newVal) {
      if (newVal) {
        if (newVal.state === 200) {
          this.$store.dispatch('user/clearLogOutUserResult');
          this.$router.push('/login');
        }
      }
    },
  },

  created() {
    this.$store.dispatch('prefecture/setPrefectureList');
    this.$store.dispatch('user/setCurrentUserDetail');
  },

  mounted() {
    this.popupItem = this.$refs.alertSuccess;
  },

  methods: {
    logout() {
      this.$store.dispatch('user/logOutUser');
    },
    goToUserMenu() {
      this.$router.push('./user-menu');
    },
    goToPointBuy() {
      this.$router.push('./buy-point');
    },
    goToBuyProductHistory() {
      this.$router.push('/buy-product-history');
    },
    goToPointHistory() {
      this.$router.push('./buy-point-history');
    },
    goToUserGuide() {
      this.$router.push('/user-guide');
    },
    goContactUs() {
      window.open('https://www.iranoan.com/contacts', '_blank');
    },
    clickMaintainRank() {
      this.$store.dispatch('user/setCurrentUserDetail');
      if (this.currentUser.coin < this.rankCoin) {
        window.alert('コインが不足しています');
        return;
      }
      this.isOpenConfirm = true;
    },
    buyRank() {
      (async () => {
        this.isOpenConfirm = false;
        const postParams = {
          coin: this.rankCoin,
          next_month_rank: this.rankNextMonth,
        };
        await this.$store.dispatch('user/buyRank', { postParams });
        const { buyRankResult } = this;
        const isSuceess = buyRankResult.state === 200 && buyRankResult.result === '1';
        if (isSuceess) {
          this.isOpenConp = true;
          this.$store.dispatch('user/setCurrentUser');
          this.$store.dispatch('user/setCurrentUserDetail');
        } else {
          const { count } = buyRankResult.errors;
          window.alert(count);
        }
      })();
    },
  },
};
</script>

<style lang="scss" scoped>
.my-page{
  width: 100%;
  max-width: 600px;
  min-height: calc(100vh - 303px);
  padding-top: 30px;
  padding-bottom: 50px;
  margin: 0px auto;
  .address_change {
    margin-bottom: 5px;
    text-align: center;
    a {
      color: #000 !important;
    }
  }
  .name_area {
    text-align: center;
    font-size: 26px;
    padding: 50px 0 40px 0;
    span {
      color: $black;
      border-bottom: solid 1px $black;
      padding: 0 20px;
    }
  }
  .gray_area {
    padding: 30px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: space-around;
    .label {
      display: flex;
      justify-content: center;
      flex-direction: column;
      span {
        font-size: 22px;
        // font-weight: bold;
        color: $black;
      }
      .point_area {
        // margin-left: 20px;
        display: flex;
        align-items: baseline;
        span {
          &:nth-child(1) {
            font-size: 44px;
          }
        }
      }
    }
  }
  .link_area {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    .link {
      border-left: solid 1px $black;
      font-size: 14px;
      text-align: center;
      padding: 20px 0;
      width: 100%;
      cursor: pointer;
      transition: .4s;
      &:hover {
        text-decoration: underline;
      }
      &:last-child {
        border-right: solid 1px $black;
      }
    }
  }
  .button_area {
    // margin-top: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    .square-button {
      max-width: 200px;
      width: 100%;
    }
    .logout {
      background-color: $white;
      color: #000;
      border: solid 1px #000;
      img {
        margin-left: 10px;
      }
    }
  }
}
.overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0px 30px;

  .modal {
    padding: 40px 28px;
    font-family: $notoSans;
    color: #000;
    text-align: center;
    width: 586px;

    .modal-title {
      font-size: 24px;
      font-weight: bold;
    }

    .text {
      display: flex;
      flex-direction: column;
      margin-top: 20px;
      font-size: 16px;
      font-weight: 400;
      line-height: 28px;
    }

    .modal-button-area {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 30px;

      .square-button {
        width: 120px;
        height: 50px;

        &+.square-button {
          margin-left: 20px;
        }
      }
    }
  }
}
.pack-confirm {
  background-color: #fff;
  color: #000;
  position: absolute;
  height: auto;

  .close {
    /* 閉じるボタンを右上に表示 */
    position: absolute;
    top: 10px;
    right: 10px;
    /* 閉じるボタンの大きさ */
    font-size: 24px;
    /* 閉じるボタンのカーソル */
    cursor: pointer;
  }

  .bay_area {
    background-color: $mainGray;
    padding: 20px 0;
    border-radius: 9px;
    & + .bay_area {
      margin-top: 10px;
    }
  }

  .modal-subtitle {
    width: 50%;
    margin: auto;
    background-color: #000;
    color: #fff;
  }

  .use_num {
    font-size: 40px;
    font-weight: bold;
    color: #000;
  }

  .use-point {
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 10px 0;
    width: 50%;
    margin: auto;
  }

  .bay-button {
    width: 100%;
    height: 52px;
  }
}
@media screen and (max-width: $tabletWidth) {
  .my-page{
    .name_area {
      padding: 0 0 20px 0;
    }
    .gray_area {
      padding: 30px;
      margin: 0 45px 20px 45px;
      flex-direction: column;
      .label {
        flex-direction: column;
        span {
          font-size: 18px;
        }
        .point_area {
          margin-left: 0;
          span {
            &:nth-child(1) {
              font-size: 30px;
            }
          }
        }
      }
    }
    .link_area {
      flex-direction: column;
      padding: 0 10px;
      .link {
        border-top: solid 1px $black;
        border-left: none;
        text-align: left;
        padding-left: 12px;
        &:hover {
          text-decoration: none;
        }
        &:active {
          text-decoration: underline;
        }
        &:last-child {
          border-right: none;
          border-bottom: solid 1px $black;
        }
      }
    }
    .button_area {
      margin-top: 20px;
    }
  }
  .overlay {
    .modal {
      width: 80%;
    }
  }
  .pack-confirm {
    .modal-subtitle {
      font-size: 12px;
    }

    .use_num {
      font-size: 20px;
    }

    .bay-button {
      height: 45px;
    }
  }
}
.mt-40 {
  margin-top: 40px;
}
</style>
